import { forwardRef } from "react";
import { AlertCircle, CheckCircle2, ChevronDown } from "lucide-react";

export const inputClass = (error) =>
  `w-full h-12 rounded-md border bg-white px-4 text-[15px] text-[#111827] placeholder:text-slate-400 transition-[border-color,box-shadow] focus:outline-none focus:ring-1 ${
    error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-slate-300 focus:border-[#2563EB] focus:ring-[#2563EB]"
  }`;

export const Field = ({ id, label, required, error, hint, children, className = "" }) => (
  <div className={className}>
    <label htmlFor={id} className="mb-2 block text-sm font-medium text-[#111827]">
      {label}
      {required ? (
        <span className="ml-0.5 text-[#2563EB]" aria-hidden="true">*</span>
      ) : (
        <span className="ml-1.5 font-normal text-slate-400">(optional)</span>
      )}
    </label>
    {children}
    {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    {error && (
      <p id={`${id}-error`} role="alert" className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600" data-testid={`${id}-error`}>
        <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" /> {error.message}
      </p>
    )}
  </div>
);

export const TextInput = forwardRef(({ error, ...props }, ref) => (
  <input ref={ref} className={inputClass(error)} aria-invalid={!!error} aria-describedby={error ? `${props.id}-error` : undefined} {...props} />
));
TextInput.displayName = "TextInput";

export const TextArea = forwardRef(({ error, rows = 5, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={`${inputClass(error)} h-auto py-3 leading-relaxed`}
    aria-invalid={!!error}
    aria-describedby={error ? `${props.id}-error` : undefined}
    {...props}
  />
));
TextArea.displayName = "TextArea";

export const SelectInput = forwardRef(({ error, options, placeholder = "Select", ...props }, ref) => (
  <div className="relative">
    <select ref={ref} className={`${inputClass(error)} appearance-none pr-10`} aria-invalid={!!error} {...props}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
  </div>
));
SelectInput.displayName = "SelectInput";

export const CheckRow = forwardRef(({ id, label, error, ...props }, ref) => (
  <div>
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-600">
      <input ref={ref} id={id} type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[#2563EB]" aria-invalid={!!error} {...props} />
      <span>{label}</span>
    </label>
    {error && (
      <p role="alert" className="mt-1.5 pl-7 text-xs text-red-600" data-testid={`${id}-error`}>{error.message}</p>
    )}
  </div>
));
CheckRow.displayName = "CheckRow";

export const FormAlert = ({ message, testId = "form-server-error" }) =>
  message ? (
    <div role="alert" data-testid={testId} className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  ) : null;

export const Fieldset = ({ legend, children }) => (
  <fieldset className="border-t border-slate-200 pt-8 first:border-t-0 first:pt-0">
    <legend className="mb-6 font-display text-sm font-semibold uppercase tracking-[0.16em] text-[#0B1220]">{legend}</legend>
    <div className="grid gap-6 md:grid-cols-2">{children}</div>
  </fieldset>
);

const EMAIL_MESSAGES = {
  sent: (email) => `A confirmation email has been sent to ${email}.`,
  failed: () => "We were unable to send a confirmation email at this time. Please keep your reference number for your records.",
};

export const SubmissionSuccess = ({ title, message, reference, emailStatus, email, testId, children }) => (
  <div className="py-4" role="status" aria-live="polite" data-testid={testId}>
    <CheckCircle2 className="h-10 w-10 text-[#2563EB]" strokeWidth={1.5} aria-hidden="true" />
    <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[#0B1220]">{title}</h3>
    <p className="mt-4 max-w-xl leading-relaxed text-slate-600">{message}</p>
    <div className="mt-8 inline-flex flex-col rounded-md border border-slate-200 bg-[#F4F6F8] px-5 py-4">
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Reference number</span>
      <span className="mt-1 font-display text-lg font-semibold text-[#0B1220]" data-testid="submission-reference">{reference}</span>
    </div>
    {EMAIL_MESSAGES[emailStatus] && (
      <p className="mt-6 text-sm text-slate-500" data-testid="submission-email-status">{EMAIL_MESSAGES[emailStatus](email)}</p>
    )}
    {children}
  </div>
);
